import { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';
import { createAppTheme } from '../themes/themeConfig';
import { useUniversityConfig } from './UniversityConfigProvider';
import React from 'react';
import { CssBaseline } from '@mui/material';

interface ThemeContextType {
  mode: PaletteMode;
  toggleColorMode: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggleColorMode: () => {},
});

export const useThemeMode = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { config } = useUniversityConfig();

  const getInitialTheme = (): PaletteMode => {
    try {
      const savedMode = localStorage.getItem('themeMode');
      if (savedMode === 'dark' || savedMode === 'light') {
        return savedMode as PaletteMode;
      }
    } catch (error) {
      console.error('Error accessing localStorage:', error);
    }
    return 'light'; 
  };

  const [mode, setMode] = useState<PaletteMode>(getInitialTheme);

  //  System theme detection → DISABLED completely
  useEffect(() => {
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('themeMode', mode);
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }, [mode]);

  const toggleColorMode = () => {
    if (config?.features.darkMode) {
      setMode(prev => (prev === 'light' ? 'dark' : 'light'));
    }
  };

  const theme = React.useMemo(() => {
    if (!config) return createAppTheme(mode);

    return createAppTheme(mode, {
      primary: config.theme?.primaryColor,
      secondary: config.theme?.secondaryColor,
      fontFamily: config.theme?.fonts?.primary,
    });
  }, [mode, config]);

  return (
    <ThemeContext.Provider value={{ mode, toggleColorMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
